/**
 * Blueshift appends this to every tracked link; its presence is our only signal that
 * the session originated from a marketing email. The value is never transmitted —
 * only its presence is used.
 */
const BSFT_UID_PARAM = 'bsft_uid';

/**
 * `source` for an identify fired because we detected an email-driven session.
 * Enumerated in messaging's IdentifyEventSchema alongside account_login /
 * account_update / booking — messaging rejects the event if this value is absent
 * from that enum.
 */
const EMAIL_CLICK_SOURCE = 'email_click';

/**
 * Whether this page load came from a tracked Blueshift email link.
 *
 * Separate from the reporting call so the caller can guard *before* doing any work —
 * almost every page load is not email-driven and should cost one query-string read.
 */
function isEmailDrivenSession(search) {
    return Boolean(new URLSearchParams(search).get(BSFT_UID_PARAM));
}

/**
 * Reports an email-driven session so Blueshift can stitch the anonymous Snowplow
 * cookie to the known recipient of the clicked email. See wanderu/strategy#418.
 *
 * Assumes the caller has already established this *is* an email-driven session via
 * `isEmailDrivenSession` — it does not re-check.
 *
 * Passes no email and no identityId: `identifyUser` already sends sp_domain_id /
 * sp_network_id, and messaging forwards sp_domain_id to Blueshift as its `cookie`
 * identifier. An address here would defeat the design.
 *
 * No dedupe by design. `bsft_uid` only survives in the URL while the visitor stays on
 * the landing URL (measured: ~1.6 pageviews per browser carry it), and a repeat
 * identify for the same cookie and source is idempotent — Blueshift re-asserts the
 * same profile, and duplicates cannot inflate DAU because that counts distinct users.
 *
 * Never throws; returns the outcome so the caller decides what deserves a log line.
 *
 * @returns {Promise<'sent'|'failed'>}
 */
async function reportEmailClickIdentify({ locale, currency, identify }) {
    try {
        // identifyUser swallows its own errors and resolves null on failure. It also
        // resolves the Snowplow IDs via SnowplowService.getIds(), which waits for the
        // tracker to set the sp cookie — so there is no cookie race to handle here.
        const result = await identify({ source: EMAIL_CLICK_SOURCE, locale, currency });
        return result
            ? 'sent'
            : 'failed';
    } catch {
        return 'failed';
    }
}

export {
    EMAIL_CLICK_SOURCE,
    isEmailDrivenSession,
    reportEmailClickIdentify,
};
